import { useAuth, useClerk } from '@clerk/react'
import { useMutation } from '@tanstack/react-query'
import { ArrowRight, LoaderCircle, Minus, Plus, ShoppingCart, Trash2 } from 'lucide-react'
import { ApiError, apiRequest, formatMoney, planLabels } from '../lib/api'
import { MAX_UNITS_PER_CHECKOUT, useCart } from '../lib/cart'
import { Modal } from './Modal'
import { ProductImage } from './ProductImage'

interface CheckoutResponse {
  data: { checkoutUrl: string; reused: boolean }
}

export function CartDrawer() {
  const { items, count, isOpen, closeCart, removeItem, setQuantity } = useCart()
  const { isLoaded, isSignedIn, getToken } = useAuth()
  const { openSignIn } = useClerk()

  const totalCents = items.reduce((sum, item) => sum + item.priceCents * item.quantity, 0)
  const currency = items[0]?.currency ?? 'USD'
  const overLimit = count > MAX_UNITS_PER_CHECKOUT

  const checkout = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      if (!token) throw new ApiError('Sign in to continue.', 'UNAUTHENTICATED', 401)
      return apiRequest<CheckoutResponse>('/api/checkout/session', {
        method: 'POST',
        token,
        json: { items: items.map((item) => ({ productId: item.productId, plan: item.plan, quantity: item.quantity })) },
      })
    },
    onSuccess: ({ data }) => window.location.assign(data.checkoutUrl),
  })

  const startCheckout = () => {
    if (!isLoaded || items.length === 0 || overLimit) return
    if (!isSignedIn) {
      void openSignIn({})
      return
    }
    checkout.mutate()
  }

  return (
    <Modal
      open={isOpen}
      onClose={() => !checkout.isPending && closeCart()}
      title="Your cart"
      eyebrow={`CART // ${count} ${count === 1 ? 'LICENSE' : 'LICENSES'}`}
      size="compact"
    >
      {items.length === 0 ? (
        <div className="cart-empty">
          <ShoppingCart size={22} />
          <h3>Nothing here yet.</h3>
          <p>Pick a product and a license — your key arrives minutes after checkout.</p>
          <a className="button button--primary" href="/#products" onClick={closeCart}>Browse products</a>
        </div>
      ) : (
        <>
          <div className="cart-list">
            {items.map((item) => (
              <article className="cart-row" key={`${item.productId}:${item.plan}`}>
                <span className="cart-row__icon"><ProductImage name={item.name} /></span>
                <div className="cart-row__info">
                  <strong>{item.name}</strong>
                  <small>{planLabels[item.plan]} · one-time purchase</small>
                  <div className="cart-qty" aria-label={`Quantity of ${item.name} ${planLabels[item.plan]}`}>
                    <button type="button" onClick={() => setQuantity(item.productId, item.plan, item.quantity - 1)} aria-label="Decrease quantity"><Minus size={12} /></button>
                    <span>{item.quantity}</span>
                    <button
                      type="button"
                      onClick={() => setQuantity(item.productId, item.plan, item.quantity + 1)}
                      disabled={item.quantity >= MAX_UNITS_PER_CHECKOUT}
                      aria-label="Increase quantity"
                    ><Plus size={12} /></button>
                  </div>
                </div>
                <strong className="cart-row__price">
                  {formatMoney(item.priceCents * item.quantity, item.currency)}
                  {item.quantity > 1 ? <small>{item.quantity} × {formatMoney(item.priceCents, item.currency)}</small> : null}
                </strong>
                <button
                  className="icon-button cart-row__remove"
                  type="button"
                  onClick={() => removeItem(item.productId, item.plan)}
                  aria-label={`Remove ${item.name} from cart`}
                >
                  <Trash2 size={15} />
                </button>
              </article>
            ))}
          </div>

          {overLimit ? (
            <div className="form-message form-message--error" role="alert">
              A single checkout is limited to {MAX_UNITS_PER_CHECKOUT} licenses. Remove a few to continue.
            </div>
          ) : null}

          {checkout.error ? (
            <div className="form-message form-message--error" role="alert">
              {checkout.error instanceof Error ? checkout.error.message : 'The checkout could not be started.'}
            </div>
          ) : null}

          <div className="cart-total">
            <span>Total — one-time payment</span>
            <strong>{formatMoney(totalCents, currency)}</strong>
          </div>

          <button
            className="button button--primary button--wide"
            type="button"
            onClick={startCheckout}
            disabled={checkout.isPending || overLimit}
          >
            {checkout.isPending ? <><LoaderCircle className="spin" size={17} /> Preparing Stripe…</> : <>Checkout with Stripe <ArrowRight size={17} /></>}
          </button>
          <p className="modal-legal">Secure payment handled by Stripe. All license keys arrive in one email and in your account. Nothing renews automatically.</p>
        </>
      )}
    </Modal>
  )
}
